<template>
  <div class="scroll-page-form">
    <div class="scroll-page-form__header">
      <Button
        icon="far fa-arrow-left"
        class="p-button-text p-button-rounded"
        @click="$emit('cancel')"
      />
      <div class="scroll-page-form__title">
        <h2>{{ t('flows.operators.scrollPage.label') }}</h2>
        <span class="scroll-page-form__subtitle">
          {{ t('flows.operatorKinds.trigger') }}
        </span>
      </div>
      <Button
        icon="far fa-times"
        class="p-button-text p-button-rounded"
        @click="$emit('close')"
      />
    </div>

    <div class="scroll-page-form__content">
      <div class="scroll-page-form__field">
        <label class="scroll-page-form__label">
          {{ t('flows.operators.scrollPage.percentLabel') }}
        </label>
        <div class="scroll-page-form__slider-container">
          <Slider
            v-model="localData.percent_scroll"
            :min="10"
            :max="100"
            :step="10"
            class="scroll-page-form__slider"
          />
          <span class="scroll-page-form__value">{{ localData.percent_scroll }}%</span>
        </div>
        <small
          v-if="formValidation?.percent_scroll?.$errors?.length"
          class="p-error"
        >
          {{ formValidation.percent_scroll.$errors[0].$message }}
        </small>
      </div>

      <div class="scroll-page-form__info">
        <i class="far fa-info-circle" />
        <p>{{ t('flows.operators.scrollPage.description') }}</p>
      </div>
    </div>

    <div class="scroll-page-form__footer">
      <Button
        :label="t('flows.operatorPanel.cancel')"
        class="p-button-text"
        @click="$emit('cancel')"
      />
      <Button
        :label="t('flows.operatorPanel.addStep')"
        class="p-button-success"
        @click="$emit('submit')"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import Slider from 'primevue/slider';
import type { ScrollPageData } from './index';

export default defineComponent({
  name: 'ScrollPageForm',

  components: {
    Button,
    Slider,
  },

  props: {
    modelValue: {
      type: Object as PropType<ScrollPageData>,
      default: () => ({ percent_scroll: 10 }),
    },
    formValidation: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['update:modelValue', 'cancel', 'close', 'submit'],

  setup(props, { emit }) {
    const { t } = useI18n();

    const localData = ref<ScrollPageData>({ ...props.modelValue });

    watch(localData, (newData) => {
      emit('update:modelValue', newData);
    }, { deep: true });

    watch(() => props.modelValue, (newValue) => {
      if (newValue !== localData.value) {
        localData.value = { ...newValue };
      }
    });

    return {
      t,
      localData,
    };
  },
});
</script>

<style lang="scss" scoped>
.scroll-page-form {
  display: flex;
  flex-direction: column;
  height: 100%;

  &__header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e5e7eb;

    .p-button {
      width: 32px !important;
      height: 32px !important;
      min-width: 32px !important;
    }
  }

  &__title {
    flex: 1;

    h2 {
      margin: 0;
      font-size: 0.875rem;
      font-weight: 600;
      color: #1f2937;
    }
  }

  &__subtitle {
    font-size: 0.75rem;
    color: #6b7280;
  }

  &__content {
    flex: 1;
    padding: 1rem;
    overflow-y: auto;
  }

  &__field {
    margin-bottom: 1.5rem;
  }

  &__label {
    display: block;
    margin-bottom: 0.75rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }

  &__slider-container {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  &__slider {
    flex: 1;
  }

  &__value {
    min-width: 3rem;
    font-size: 1rem;
    font-weight: 600;
    color: #3b82f6;
    text-align: right;
  }

  &__info {
    display: flex;
    gap: 0.75rem;
    padding: 1rem;
    background: #f0f9ff;
    border-radius: 0.5rem;
    color: #0369a1;

    i {
      font-size: 1.25rem;
      flex-shrink: 0;
    }

    p {
      margin: 0;
      font-size: 0.875rem;
      line-height: 1.5;
    }
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding: 1rem;
    border-top: 1px solid #e5e7eb;
  }
}
</style>
